/**
 * The leaderboard display (the venue TV and the player's phone pages): which screens there are, and the words on them.
 * Pure — no DOM — so every rule here is tested. The drawing is in views/leaderboardDisplay.ts. See docs/mockups/ for the design.
 */

import type { LeaderboardEntry } from './leaderboardClient';
import type { Theme } from './theme';

/** `live` = the season is running; `review` = it has ended and staff are checking; `final` = staff marked it final. */
export type DisplayState = 'live' | 'review' | 'final';

export interface DisplayBoard {
  state: DisplayState;
  /** This venue's 1st, 2nd, 3rd... season (periods have no name of their own). */
  season: number;
  nextStartsAt: number | null;
  period: { id: string; venue: string; startsAt: number; endsAt: number; settlement: string };
  entries: LeaderboardEntry[];
}

export interface DisplaySettings {
  screenSeconds: number;
  showTable: boolean;
  showPodium: boolean;
  theme: Theme | null;
  logoPlate: boolean;
}

export interface DisplayBranding {
  name: string;
  logoDataUrl?: string;
}

/** Seconds each screen stays up when nothing has been set (mirrors DEFAULT_DISPLAY in relay/activation.ts). */
export const DEFAULT_SCREEN_SECONDS = 10;
export const PAGE_SIZE = 25;
const DAY_MS = 24 * 60 * 60 * 1000;

export type DisplayScreen = { kind: 'table'; page: number } | { kind: 'podium' };

/**
 * The screens to rotate through. Ranking pages hold 25 each, and a page only exists when there are players for it. The podium needs
 * at least three players. Whatever the settings say, there is always at least one screen (the first ranking page).
 */
export function displayScreens(entryCount: number, settings: Pick<DisplaySettings, 'showTable' | 'showPodium'>): DisplayScreen[] {
  const podium = settings.showPodium && entryCount >= 3;
  const screens: DisplayScreen[] = [];
  if (settings.showTable || !podium) {
    const pages = Math.max(1, Math.ceil(entryCount / PAGE_SIZE));
    for (let page = 0; page < pages; page++) screens.push({ kind: 'table', page });
  }
  if (podium) screens.push({ kind: 'podium' });
  return screens;
}

/** Fixed English month names: a locale's own abbreviations differ from browser to browser ("Sep" or "Sept"), and a TV's browser is the least predictable. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayMonth = (ms: number): string => {
  const d = new Date(ms);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

/** "1 – 14 Oct 2026", or "28 Sep – 11 Oct 2026" across a month. The end shown is the last day the season is open. */
export function seasonDates(startsAt: number, endsAt: number): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt - 1);
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const from = sameMonth ? String(start.getDate()) : dayMonth(startsAt);
  return `${from} – ${dayMonth(endsAt - 1)} ${end.getFullYear()}`;
}

export function daysLeftText(endsAt: number, now: number): string {
  const days = Math.ceil((endsAt - now) / DAY_MS);
  if (days <= 1) return 'Last day';
  return `${days} days left`;
}

/** The line under "Leaderboard": which season, when, and where it stands. */
export function seasonLine(board: DisplayBoard, now: number): string {
  const head = `Season ${board.season} · ${seasonDates(board.period.startsAt, board.period.endsAt)}`;
  if (board.state === 'live') return `${head} · ${daysLeftText(board.period.endsAt, now)}`;
  if (board.state === 'review') return `${head} · Ended, final results coming soon`;
  return `${head} · Completed`;
}

export interface StripText {
  tag: string;
  text: string;
  more: string;
}

/** The badge that says what the board is: the live current standing, a finished season under review, or the final result. */
export function stripText(board: DisplayBoard, compact: boolean): StripText {
  const next = board.nextStartsAt === null ? '' : compact ? ` · next: ${dayMonth(board.nextStartsAt)}` : ` · next season starts ${dayMonth(board.nextStartsAt)}`;
  if (board.state === 'live') {
    return { tag: 'LIVE', text: compact ? 'Current standing · not final' : 'Current standing – not final, it can change until the season ends', more: '' };
  }
  if (board.state === 'review') return { tag: '⏳ UNDER REVIEW', text: 'Season ended – final results coming soon', more: next };
  return { tag: '🏁 FINAL', text: 'Season completed – final results', more: next };
}

const clock = (ms: number): string => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

export function footerLeft(board: DisplayBoard, generatedAt: number): string {
  if (board.state === 'live') return `Live · updated ${clock(generatedAt)}`;
  return board.state === 'review' ? `Season ${board.season} · ended, under review` : `Season ${board.season} · completed`;
}

export function footerRight(board: DisplayBoard, screen: DisplayScreen): string {
  if (screen.kind === 'podium') return board.state === 'live' ? 'Top 3 right now' : board.state === 'review' ? 'Provisional top 3' : 'Season champions';
  const total = board.entries.length;
  const from = screen.page * PAGE_SIZE + 1;
  return total === 0 ? 'No games counted yet' : `Ranks ${from}–${Math.min(total, from + PAGE_SIZE - 1)} of ${total}`;
}

/** How far the podium's players are from the top, for the line under each name. Ties are said as ties. */
export function gapText(entries: LeaderboardEntry[], index: number): string {
  const first = entries[0];
  const here = entries[index];
  if (!first || !here) return '';
  if (index === 0) {
    const second = entries[1];
    if (!second) return '';
    const lead = first.points - second.points;
    return lead === 0 ? 'level with 2nd' : `${formatPoints(lead)} pts ahead of 2nd`;
  }
  const behind = first.points - here.points;
  return behind === 0 ? 'level with 1st' : `${formatPoints(behind)} pts behind 1st`;
}

export function formatPoints(points: number): string {
  return points.toLocaleString('en-US');
}

/** A TV link's address ends in `#tv=<token>`; this reads the token back out of the page's hash, or undefined. */
export const TV_HASH_PREFIX = '#tv=';
export function tvTokenFromHash(hash: string): string | undefined {
  if (!hash.startsWith(TV_HASH_PREFIX)) return undefined;
  const token = decodeURIComponent(hash.slice(TV_HASH_PREFIX.length)).trim();
  return token.length > 0 ? token : undefined;
}
